import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const cls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2 text-sm focus:border-[#008057] outline-none mt-1";
const STRENGTH_BARS = ["b1", "b2", "b3", "b4", "b5"];
const LEVELS = [
  { label: "Very weak", color: "bg-red-500" },
  { label: "Weak", color: "bg-orange-500" },
  { label: "Fair", color: "bg-amber-500" },
  { label: "Good", color: "bg-lime-500" },
  { label: "Strong", color: "bg-[#008057]" },
  { label: "Very strong", color: "bg-[#008057]" },
];
const RULES = [(pw) => pw.length >= 8, (pw) => pw.length >= 12, (pw) => /[a-z]/.test(pw) && /[A-Z]/.test(pw), (pw) => /\d/.test(pw), (pw) => /[^A-Za-z0-9]/.test(pw)];

export function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "" };
  const score = RULES.filter((r) => r(pw)).length;
  return { score, ...LEVELS[score] };
}

const StrengthMeter = ({ strength }) => (
  <div data-testid="cp-strength" className="-mt-2">
    <div className="flex gap-1">
      {STRENGTH_BARS.map((b, i) => <div key={b} className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength.score ? strength.color : "bg-slate-200"}`} />)}
    </div>
    <div className="text-xs text-slate-500 mt-1">Strength: <span className="font-semibold">{strength.label}</span></div>
  </div>
);

const PwField = ({ label, testId, auto, value, onChange }) => (
  <label className="block"><span className="text-sm font-semibold text-[#03281B]">{label}</span>
    <input data-testid={testId} type="password" autoComplete={auto} className={cls} value={value} onChange={(e) => onChange(e.target.value)} required /></label>
);

export function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters long."); return; }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-[#03281B]">My Account</h1>
      <p className="text-slate-500 mt-1 text-sm">Change your admin login password.</p>
      <form onSubmit={submit} className="mt-6 max-w-md rounded-2xl bg-white border border-[#E2EBF0] p-6 space-y-4" data-testid="change-password-form">
        <PwField label="Current password" testId="cp-current" auto="current-password" value={current} onChange={setCurrent} />
        <PwField label="New password" testId="cp-new" auto="new-password" value={next} onChange={setNext} />
        {next && <StrengthMeter strength={passwordStrength(next)} />}
        <PwField label="Confirm new password" testId="cp-confirm" auto="new-password" value={confirm} onChange={setConfirm} />
        <p className="text-xs text-slate-400">Minimum 8 characters. You will keep your current session after changing.</p>
        <button data-testid="cp-submit" type="submit" disabled={saving} className="rounded-full bg-[#008057] text-white px-8 py-3 font-bold disabled:opacity-60">{saving ? "Updating..." : "Update Password"}</button>
      </form>
    </div>
  );
}
